
import React from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

interface DateRangeFilterProps {
  dateRange: DateRange;
  onDateChange: (dateRange: DateRange) => void;
  isMobile?: boolean;
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  dateRange,
  onDateChange,
  isMobile = false
}) => {
  // Stop propagation to prevent calendar from closing
  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  return (
    <div className={`flex flex-row space-x-2 ${isMobile ? 'w-full' : ''}`}>
      <div className={isMobile ? 'w-full flex-1' : 'w-40'}>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start text-left font-normal h-10 pl-3 pr-3"
            >
              <div className="flex items-center">
                <CalendarIcon className="h-4 w-4 text-gray-500" />
                <Separator orientation="vertical" className="mx-2 h-4" />
                <span className="truncate">
                  {dateRange.from ? (
                    format(dateRange.from, 'PP')
                  ) : (
                    <span className="text-muted-foreground">is after</span>
                  )}
                </span>
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            className="w-auto p-0" 
            align="start"
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
          >
            <div className="calendar-wrapper pointer-events-auto" onClick={stopPropagation} onMouseDown={stopPropagation}>
              <Calendar
                mode="single"
                selected={dateRange.from}
                onSelect={(date) =>
                  onDateChange({ ...dateRange, from: date })
                }
                initialFocus
                className="rounded-md"
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>
      
      {isMobile && <span className="flex items-center text-gray-400">-</span>}

      <div className={isMobile ? 'w-full flex-1' : 'w-40'}>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start text-left font-normal h-10 pl-3 pr-3"
            >
              <div className="flex items-center">
                <CalendarIcon className="h-4 w-4 text-gray-500" />
                <Separator orientation="vertical" className="mx-2 h-4" />
                <span className="truncate">
                  {dateRange.to ? (
                    format(dateRange.to, 'PP')
                  ) : (
                    <span className="text-muted-foreground">is before</span>
                  )}
                </span>
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            className="w-auto p-0" 
            align="start"
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
          >
            <div className="calendar-wrapper pointer-events-auto" onClick={stopPropagation} onMouseDown={stopPropagation}>
              <Calendar
                mode="single"
                selected={dateRange.to}
                onSelect={(date) =>
                  onDateChange({ ...dateRange, to: date })
                }
                initialFocus
                className="rounded-md"
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
